/**
 * OKR Node Tooltip Component
 * Shows detailed OKR information on node hover
 */

import React from 'react';
import { OKR } from '../../types/okr';
import { Badge } from '../common/Badge';
import { keyResultProgressPercent } from '../../utils/progress';

interface OKRNodeTooltipProps {
  okr: OKR;
  position: { x: number; y: number };
}

export const OKRNodeTooltip: React.FC<OKRNodeTooltipProps> = ({ okr, position }) => {
  // Get status badge variant
  const getStatusVariant = (status: string): 'success' | 'warning' | 'danger' | 'secondary' => {
    const variants: Record<string, 'success' | 'warning' | 'danger' | 'secondary'> = {
      on_track: 'success',
      at_risk: 'warning',
      off_track: 'danger',
      completed: 'success',
      active: 'success',
      draft: 'secondary',
      closed: 'secondary', // S1-g — see OKRDetailSidebar for why this is spelled out.
      deleted: 'secondary',
    };
    return variants[status] || 'secondary';
  };

  // Get type-specific colors
  const getTypeColors = (type: string) => {
    switch (type) {
      case 'company':
        return {
          bg: 'bg-blue-50',
          border: 'border-blue-200',
          header: 'bg-blue-100',
          text: 'text-blue-900',
          progressBg: 'bg-blue-200',
          progressFill: 'bg-blue-600',
        };
      case 'team':
      case 'department':
        return {
          bg: 'bg-green-50',
          border: 'border-green-200',
          header: 'bg-green-100',
          text: 'text-green-900',
          progressBg: 'bg-green-200',
          progressFill: 'bg-green-600',
        };
      case 'individual':
        return {
          bg: 'bg-purple-50',
          border: 'border-purple-200',
          header: 'bg-purple-100',
          text: 'text-purple-900',
          progressBg: 'bg-purple-200',
          progressFill: 'bg-purple-600',
        };
      default:
        return {
          bg: 'bg-gray-50',
          border: 'border-gray-200',
          header: 'bg-gray-100',
          text: 'text-gray-900',
          progressBg: 'bg-gray-200',
          progressFill: 'bg-gray-600',
        };
    }
  };

  const colors = getTypeColors(okr.type);
  const keyResults = okr.keyResults || [];

  return (
    <div
      className={`absolute z-50 w-80 ${colors.bg} ${colors.border} border-2 rounded-lg shadow-2xl overflow-hidden pointer-events-none`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        transform: 'translate(10px, -50%)',
      }}
    >
      {/* Header */}
      <div className={`${colors.header} px-4 py-3 border-b ${colors.border}`}>
        <div className="flex items-start justify-between gap-2 mb-2">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className={`text-xs font-semibold ${colors.text} uppercase tracking-wide`}>
                {okr.type} OKR
              </span>
              <Badge variant={getStatusVariant(okr.status)} size="sm">
                {okr.status.replace('_', ' ')}
              </Badge>
            </div>
            <h3 className={`font-semibold ${colors.text} text-sm leading-tight`}>
              {okr.title}
            </h3>
          </div>
        </div>

        <div className="flex items-center justify-between text-xs">
          <span className={colors.text}>
            <strong>Owner:</strong> {okr.owner.firstName} {okr.owner.lastName}
          </span>
          <span className={`font-bold ${colors.text}`}>
            {okr.progress || 0}%
          </span>
        </div>
      </div>

      {/* Description */}
      {okr.description && (
        <div className="px-4 py-2 border-b border-gray-200">
          <p className="text-xs text-gray-700 line-clamp-2">{okr.description}</p>
        </div>
      )}

      {/* Key Results */}
      {keyResults.length > 0 && (
        <div className="px-4 py-3">
          <h4 className={`text-xs font-semibold ${colors.text} mb-2`}>
            Key Results ({keyResults.length})
          </h4>
          <div className="space-y-2 max-h-48 overflow-y-auto">
            {keyResults.map((kr) => {
              const krProgress = keyResultProgressPercent(kr);
              return (
                <div key={kr.id} className="text-xs">
                  <div className="flex items-start justify-between mb-1">
                    <span className="font-medium text-gray-900 flex-1 pr-2">
                      {kr.title}
                    </span>
                    <span className="font-semibold text-gray-700 whitespace-nowrap">
                      {krProgress}%
                    </span>
                  </div>
                  <div className="flex items-center gap-2 mb-1">
                    <div className={`flex-1 ${colors.progressBg} rounded-full h-1.5 overflow-hidden`}>
                      <div
                        className={`h-full ${colors.progressFill} rounded-full transition-all duration-300`}
                        style={{ width: `${krProgress}%` }}
                      />
                    </div>
                  </div>
                  <div className="text-[10px] text-gray-600">
                    {kr.currentValue} / {kr.targetValue} {kr.unit}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* No Key Results Message */}
      {keyResults.length === 0 && (
        <div className="px-4 py-3">
          <p className="text-xs text-gray-500 italic">No key results defined</p>
        </div>
      )}

      {/* Footer with additional info */}
      {okr.owner.department && (
        <div className={`px-4 py-2 ${colors.header} border-t ${colors.border}`}>
          <p className="text-[10px] text-gray-600">
            <strong>Department:</strong> {okr.owner.department}
          </p>
        </div>
      )}
    </div>
  );
};
